import { useState, useEffect, useRef } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  X, Send, Bot, User, Sparkles, Trash2, Tag, Search, AlertCircle, Loader2, Plus
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EmailChatPanel({ actorId, isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Contact picker state
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [contactPickerData, setContactPickerData] = useState(null);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [manualEmail, setManualEmail] = useState("");

  // CRITICAL: Clear all state on mount and initialize fresh
  useEffect(() => {
    // Force clear everything first
    setMessages([]);
    setInput("");
    setLoading(false);
    
    // Initialize with welcome message
    const welcomeMessage = {
      role: "assistant",
      content: getWelcomeMessage(),
      timestamp: new Date().toISOString(),
      isWelcome: true
    };
    
    setMessages([welcomeMessage]);
  }, []); // Run only once on mount

  useEffect(() => {
    if (isOpen && actorId) {
      // Already initialized in mount effect
    } else if (!isOpen) {
      // Clear messages when closing
      setMessages([]);
      setInput("");
    }
  }, [isOpen, actorId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const getWelcomeMessage = () => {
    return `👋 **Welcome to AI Email Assistant!**

I'm your intelligent email companion. I can help you with:

**🔍 Search & Find**
• Search emails using natural language
• Find emails by sender, date, or content
• Get detailed email information

**📊 Analyze & Statistics**
• Get inbox insights and statistics
• View top email senders
• Analyze email patterns

**✍️ Draft & Reply**
• Generate professional email drafts
• Reply to emails with context
• Learn and adapt to your writing style

---

**How to use:**
Simply type what you need in natural language!

**Examples:**
• "Show me unread emails from this week"
• "Who are my top 5 senders?"
• "Draft an email to John about the project update"
• "Search emails about budget"
• "Analyze my inbox"

*I have access to all your emails and can provide intelligent assistance!*`;
  };

  const fetchChatHistory = async () => {
    // Keep for potential future use, but don't call automatically
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/admin/ai-email/chat/history/${actorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.messages || [];
    } catch (error) {
      console.error("Failed to fetch chat history", error);
      return [];
    }
  };

  const sendMessage = async (messageText = input) => {
    if (!messageText.trim() || sending) return;

    const userMessage = {
      role: "user",
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setSending(true);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/ai-email/chat?actor_id=${actorId}&message=${encodeURIComponent(messageText)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const assistantMessage = {
        role: "assistant",
        content: response.data.response || "I apologize, but I couldn't process that request.",
        timestamp: new Date().toISOString(),
        function_calls: response.data.function_calls || [],
        error: response.data.error,
        draft_data: response.data.draft_data // Include draft data if present
      };
      
      setMessages((prev) => [...prev, assistantMessage]);
      
      // Check if contact selection is needed
      if (response.data.needs_contact_selection) {
        setContactPickerData({
          message: response.data.message,
          searchTerm: response.data.search_term,
          suggestedContacts: response.data.suggested_contacts || [],
          allowManualEntry: response.data.allow_manual_entry,
          originalInput: messageText
        });
        setShowContactPicker(true);
      }

      // Show function call results as toasts
      if (response.data.function_calls && response.data.function_calls.length > 0) {
        response.data.function_calls.forEach(fc => {
          if (fc.result.success) {
            toast.success(`Action completed: ${fc.function_name}`);
          }
        });
      }
    } catch (error) {
      console.error("Failed to send message", error);
      toast.error("Failed to send message to AI");
      
      // Add error message
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "I'm sorry, I encountered an error. Please try again.",
        timestamp: new Date().toISOString(),
        error: true
      }]);
    } finally {
      setSending(false);
    }
  };

  const approveDraft = async (draftData) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/ai-email/approve-draft`,
        draftData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Draft approved! Check AI Tasks to send it.");
    } catch (error) {
      console.error("Failed to approve draft", error);
      toast.error("Failed to approve draft");
    }
  };

  const handleContactSelection = async () => {
    if (!selectedContactId && !manualEmail) {
      toast.error("Please select a contact or enter an email address");
      return;
    }

    try {
      setSending(true);
      
      // Get selected contact email or use manual entry
      let recipientEmail = manualEmail;
      
      if (selectedContactId) {
        const selectedContact = contactPickerData.suggestedContacts.find(
          c => c.id === selectedContactId
        );
        if (selectedContact) {
          recipientEmail = selectedContact.email;
        }
      }

      // Close picker and reset
      setShowContactPicker(false);
      setSelectedContactId(null);
      setManualEmail("");
      setContactPickerData(null);

      // Add a system message showing the selected contact
      const selectionMessage = {
        role: "assistant",
        content: `✓ Selected contact: ${recipientEmail}\n\nGenerating draft...`,
        timestamp: new Date().toISOString()
      };
      setMessages((prev) => [...prev, selectionMessage]);

      // Create a modified request with the FULL email address (not just replacing the name)
      // Extract the context from original request (everything after "about/regarding/for")
      const originalRequest = contactPickerData.originalInput;
      let context = originalRequest;
      
      // Try to extract context after common keywords
      const contextMatches = originalRequest.match(/about|regarding|for|on\s+(.+)/i);
      if (contextMatches) {
        context = contextMatches[0];
      }
      
      // Create a clean request with the resolved email
      const modifiedRequest = `Draft an email to ${recipientEmail} ${context}`;

      // Send the modified request (message as query parameter, not body)
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/ai-email/chat?actor_id=${actorId}&message=${encodeURIComponent(modifiedRequest)}`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const assistantMessage = {
        role: "assistant",
        content: response.data.response || "I apologize, but I couldn't process that request.",
        timestamp: new Date().toISOString(),
        function_calls: response.data.function_calls || [],
        error: response.data.error,
        draft_data: response.data.draft_data
      };
      
      setMessages((prev) => [...prev, assistantMessage]);
      
      // Check if another contact selection is needed (shouldn't happen, but just in case)
      if (response.data.needs_contact_selection) {
        toast.error("Contact resolution failed. Please try again.");
      }
      
    } catch (error) {
      console.error("Failed to handle contact selection", error);
      toast.error("Failed to process contact selection");
    } finally {
      setSending(false);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setInput("");
    
    // Add welcome message
    const welcomeMessage = {
      role: "assistant",
      content: getWelcomeMessage(),
      timestamp: new Date().toISOString(),
      isWelcome: true
    };
    setMessages([welcomeMessage]);
    
    toast.success("Started new chat");
  };

  const handleClearChat = async () => {
    if (!confirm("Clear chat history?\n\nThis will:\n• Remove all messages from this session\n• Clear backend conversation context\n\nThis cannot be undone.")) {
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API}/admin/ai-email/chat/history/${actorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Clear and re-add welcome message
      setMessages([]);
      const welcomeMessage = {
        role: "assistant",
        content: getWelcomeMessage(),
        timestamp: new Date().toISOString(),
        isWelcome: true
      };
      setMessages([welcomeMessage]);
      
      toast.success("Chat cleared! Starting fresh conversation.");
    } catch (error) {
      console.error("Failed to clear chat history", error);
      toast.error("Failed to clear chat history");
    }
  };

  const handleClose = async () => {
    // Clear messages immediately for next open
    setMessages([]);
    setInput("");
    
    // Clear backend history in background
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API}/admin/ai-email/chat/history/${actorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error("Failed to clear chat history on close", error);
    }
    
    // Close the panel
    onClose();
  };

  const quickActions = [
    { 
      label: "📊 Analyze", 
      message: "Analyze my inbox and give me insights",
      tooltip: "Get inbox statistics and insights"
    },
    { 
      label: "✉️ Unread", 
      message: "Show me my unread emails",
      tooltip: "View all unread emails"
    },
    { 
      label: "✍️ Draft", 
      message: "Help me draft an email",
      tooltip: "Create a new email draft"
    },
    { 
      label: "🔍 Search", 
      message: "Search my emails",
      tooltip: "Find specific emails"
    },
    { 
      label: "👥 Senders", 
      message: "Who are my top senders?",
      tooltip: "View top email senders"
    }
  ];

  if (!isOpen) return null;

  return (
    <div className="email-chat-panel fixed right-0 top-0 h-screen w-[450px] bg-white border-l shadow-2xl flex flex-col overflow-hidden" style={{ zIndex: 99999 }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5" />
          <div>
            <h2 className="font-semibold">AI Email Assistant</h2>
            <p className="text-xs text-blue-100">Powered by OpenRouter</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleNewChat} 
            className="text-white hover:bg-white/20"
            title="Start new chat"
          >
            <Plus className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleClearChat} 
            className="text-white hover:bg-white/20"
            title="Clear chat history"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleClose} className="text-white hover:bg-white/20">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="p-3 bg-slate-50 border-b">
        <p className="text-xs text-muted-foreground mb-2">Quick actions:</p>
        <div className="flex flex-wrap gap-2">
          {quickActions.map((action) => (
            <Button
              key={action.label}
              variant="outline"
              size="sm"
              onClick={() => sendMessage(action.message)}
              disabled={sending}
              className="text-xs"
            >
              <Sparkles className="w-3 h-3 mr-1" />
              {action.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col h-full p-4">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 mb-4">
                <Bot className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-lg text-slate-800 mb-2">AI Email Assistant</h3>
              <p className="text-sm text-slate-600">
                I can help you manage your emails efficiently. Choose an action below or ask me anything!
              </p>
            </div>

            <div className="flex-1 overflow-y-auto space-y-3">
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">📊 Analyze & Statistics</h4>
                <div className="grid grid-cols-1 gap-2">
                  <Button
                    variant="outline"
                    className="justify-start text-left h-auto py-3 px-4"
                    onClick={() => sendMessage("Analyze my inbox")}
                  >
                    <div className="flex items-start gap-3 w-full">
                      <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                        📊
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">Analyze Inbox</div>
                        <div className="text-xs text-slate-500">Get statistics and insights</div>
                      </div>
                    </div>
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="justify-start text-left h-auto py-3 px-4"
                    onClick={() => sendMessage("Who are my top senders?")}
                  >
                    <div className="flex items-start gap-3 w-full">
                      <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                        👥
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">Top Senders</div>
                        <div className="text-xs text-slate-500">See who emails you most</div>
                      </div>
                    </div>
                  </Button>

                  <Button
                    variant="outline"
                    className="justify-start text-left h-auto py-3 px-4"
                    onClick={() => sendMessage("Show me my unread emails")}
                  >
                    <div className="flex items-start gap-3 w-full">
                      <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                        ✉️
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">Unread Emails</div>
                        <div className="text-xs text-slate-500">View all unread emails</div>
                      </div>
                    </div>
                  </Button>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">🔍 Search & Find</h4>
                <div className="grid grid-cols-1 gap-2">
                  <Button
                    variant="outline"
                    className="justify-start text-left h-auto py-3 px-4"
                    onClick={() => sendMessage("Search emails about project")}
                  >
                    <div className="flex items-start gap-3 w-full">
                      <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
                        🔍
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">Search Emails</div>
                        <div className="text-xs text-slate-500">Find emails by keyword or content</div>
                      </div>
                    </div>
                  </Button>

                  <Button
                    variant="outline"
                    className="justify-start text-left h-auto py-3 px-4"
                    onClick={() => sendMessage("Find emails from John this week")}
                  >
                    <div className="flex items-start gap-3 w-full">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                        📧
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">Find by Sender</div>
                        <div className="text-xs text-slate-500">Search emails from specific people</div>
                      </div>
                    </div>
                  </Button>

                  <Button
                    variant="outline"
                    className="justify-start text-left h-auto py-3 px-4"
                    onClick={() => sendMessage("Show me emails from last week")}
                  >
                    <div className="flex items-start gap-3 w-full">
                      <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                        📅
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">Find by Date</div>
                        <div className="text-xs text-slate-500">Search emails by time period</div>
                      </div>
                    </div>
                  </Button>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">✍️ Draft & Reply</h4>
                <div className="grid grid-cols-1 gap-2">
                  <Button
                    variant="outline"
                    className="justify-start text-left h-auto py-3 px-4"
                    onClick={() => sendMessage("Help me draft an email")}
                  >
                    <div className="flex items-start gap-3 w-full">
                      <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                        ✍️
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">Draft New Email</div>
                        <div className="text-xs text-slate-500">Create professional email drafts</div>
                      </div>
                    </div>
                  </Button>

                  <Button
                    variant="outline"
                    className="justify-start text-left h-auto py-3 px-4"
                    onClick={() => sendMessage("Draft an email to John about the meeting")}
                  >
                    <div className="flex items-start gap-3 w-full">
                      <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center flex-shrink-0">
                        📝
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">Draft with Context</div>
                        <div className="text-xs text-slate-500">Create drafts with specific details</div>
                      </div>
                    </div>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex gap-3 ${message.role === "user" ? "flex-row-reverse" : ""}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  message.role === "user" 
                    ? "bg-blue-600 text-white" 
                    : message.error 
                      ? "bg-red-100 text-red-600"
                      : "bg-purple-100 text-purple-600"
                }`}>
                  {message.role === "user" ? (
                    <User className="w-4 h-4" />
                  ) : message.error ? (
                    <AlertCircle className="w-4 h-4" />
                  ) : (
                    <Bot className="w-4 h-4" />
                  )}
                </div>
                <div className={`flex-1 ${message.role === "user" ? "text-right" : ""}`}>
                  <div
                    className={`inline-block max-w-[85%] p-3 rounded-lg ${
                      message.role === "user"
                        ? "bg-blue-600 text-white"
                        : message.error
                          ? "bg-red-50 border border-red-200 text-red-800"
                          : "bg-slate-100 text-slate-800"
                    }`}
                  >
                    <div className="text-sm prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {message.content}
                      </ReactMarkdown>
                    </div>
                    {message.function_calls && message.function_calls.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-slate-300">
                        {message.function_calls.map((fc, fcIndex) => (
                          <Badge key={fcIndex} variant="outline" className="text-xs mr-1">
                            {fc.function_name}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {message.draft_data && (
                      <div className="mt-2 pt-2 border-t border-slate-300">
                        <Button
                          size="sm"
                          onClick={() => approveDraft(message.draft_data)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          Approve Draft
                        </Button>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center">
                  <Bot className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <div className="inline-block bg-slate-100 p-3 rounded-lg">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <div className="p-4 border-t bg-white">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendMessage();
          }}
          className="flex gap-2"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about your emails..."
            disabled={sending}
            className="flex-1"
          />
          <Button type="submit" disabled={sending || !input.trim()}>
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </form>
        <p className="text-xs text-muted-foreground mt-2">
          Try: &quot;How many emails are there?&quot; or &quot;Delete promotional emails&quot;
        </p>
      </div>

      {/* Contact Picker Modal */}
      {showContactPicker && contactPickerData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Select Contact</h3>
            <p className="text-sm text-gray-600 mb-4">{contactPickerData.message}</p>
            
            {/* Radio button list */}
            {contactPickerData.suggestedContacts.length > 0 && (
              <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
                {contactPickerData.suggestedContacts.map((contact) => (
                  <label
                    key={contact.id}
                    className={`flex items-start p-3 border rounded cursor-pointer hover:bg-gray-50 ${
                      selectedContactId === contact.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                    }`}
                  >
                    <input
                      type="radio"
                      name="contact"
                      value={contact.id}
                      checked={selectedContactId === contact.id}
                      onChange={() => {
                        setSelectedContactId(contact.id);
                        setManualEmail(""); // Clear manual entry
                      }}
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{contact.name}</div>
                      <div className="text-sm text-gray-600">{contact.email}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        Emails: {contact.email_count_received || 0} received, {contact.email_count_sent || 0} sent
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            {/* Manual entry option */}
            {contactPickerData.allowManualEntry && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Or enter email address manually:
                </label>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={manualEmail}
                  onChange={(e) => {
                    setManualEmail(e.target.value);
                    setSelectedContactId(null); // Clear radio selection
                  }}
                  className="w-full"
                />
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowContactPicker(false);
                  setSelectedContactId(null);
                  setManualEmail("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleContactSelection}
                disabled={!selectedContactId && !manualEmail}
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
